import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConfigurationModal = ({ isOpen, onClose, processor, onSave }) => {
  const [config, setConfig] = useState({
    apiKey: processor?.apiKey || '',
    secretKey: '',
    webhookUrl: processor?.webhookUrl || '',
    testMode: processor?.testMode || false,
    defaultCurrency: 'USD',
    autoCapture: true,
    send3DSecure: true
  });

  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      onSave(config);
    } catch (error) {
      console.error('Error saving configuration:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {processor && (
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name={processor?.icon} size={20} className="text-primary" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {processor ? `Configure ${processor?.name}` : 'Add Payment Processor'}
              </h3>
              <p className="text-sm text-muted-foreground">Set up your payment gateway credentials</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* API Credentials */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Key" size={16} />
              API Credentials
            </h4>
            
            <Input
              label="Publishable Key / API Key"
              value={config?.apiKey}
              onChange={(e) => setConfig({ ...config, apiKey: e?.target?.value })}
              placeholder="pk_test_***************"
              description="Your public API key for client-side integration"
            />

            <Input
              label="Secret Key"
              type="password"
              value={config?.secretKey}
              onChange={(e) => setConfig({ ...config, secretKey: e?.target?.value })}
              placeholder="sk_test_***************"
              description="Your secret API key (stored securely)"
            />
          </div>

          {/* Webhook Configuration */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Webhook" size={16} />
              Webhook Configuration
            </h4>
            
            <Input
              label="Webhook URL"
              value={config?.webhookUrl}
              onChange={(e) => setConfig({ ...config, webhookUrl: e?.target?.value })}
              placeholder="https://yourapp.com/webhooks/processor"
              description="URL to receive payment notifications"
            />

            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Webhook Events to Subscribe:</p>
              <div className="space-y-1 text-xs text-foreground">
                <p>✓ payment_intent.succeeded</p>
                <p>✓ payment_intent.payment_failed</p>
                <p>✓ charge.refunded</p>
                <p>✓ customer.subscription.created</p>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Settings" size={16} />
              Payment Settings
            </h4>

            <Select
              label="Default Currency"
              options={[
                { value: 'USD', label: 'USD - US Dollar' },
                { value: 'EUR', label: 'EUR - Euro' },
                { value: 'GBP', label: 'GBP - British Pound' },
                { value: 'INR', label: 'INR - Indian Rupee' }
              ]}
              value={config?.defaultCurrency}
              onChange={(value) => setConfig({ ...config, defaultCurrency: value })}
              description="Default currency for transactions"
            />

            <div className="space-y-3">
              <Checkbox
                label="Enable Test Mode"
                checked={config?.testMode}
                onChange={(e) => setConfig({ ...config, testMode: e?.target?.checked })}
                description="Use test API keys and sandbox environment"
              />

              <Checkbox
                label="Auto-capture Payments"
                checked={config?.autoCapture}
                onChange={(e) => setConfig({ ...config, autoCapture: e?.target?.checked })}
                description="Automatically capture authorized payments"
              />

              <Checkbox
                label="Enable 3D Secure"
                checked={config?.send3DSecure}
                onChange={(e) => setConfig({ ...config, send3DSecure: e?.target?.checked })}
                description="Require additional authentication for card payments"
              />
            </div>
          </div>

          {/* Help Section */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Need Help?</p>
                <p>
                  Visit the {processor?.name || 'payment processor'} documentation to find your API keys and
                  set up webhooks. Make sure to use test keys in development.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            iconName={saving ? "Loader2" : "Save"}
            iconClassName={saving ? "animate-spin" : ""}
            onClick={handleSave}
            disabled={saving || !config?.apiKey || !config?.secretKey}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationModal;